import { useEffect } from 'react'
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { QueryParamProvider } from 'use-query-params'
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6'

import Account from './components/Account/Account'
import Actions from './components/Actions/Actions'
import Admin from './components/Admin/Admin'
import Analytics from './components/Analytics/Analytics'
import Configuration from './components/Configuration/Configuration'
import Conversations from './components/Conversations/Conversations'
import DataSources from './components/DataSources/DataSources'
import Faq from './components/Faq/Faq'
import Layout from './components/Layout/Layout'
import Login from './components/Login/Login'
import Lookup from './components/Lookup/Lookup'
import Messages from './components/Messages/Messages'
import { MessageTemplates } from './components/MessageTemplates/MessageTemplates'
import NLUPage from './components/NLUData/NLUData'
import NLUStats from './components/NLUData/NLUStats/NLUStats'
import NotFound from './components/NotFound/NotFound'
import RequireAuth from './components/RequireAuth/RequireAuth'
import Responses from './components/Responses/Responses'
import Rules from './components/Rules/Rules'
import SlotsForms from './components/SlotsForms/SlotsForms'
import Users from './components/Users/Users'
import { IRoleEnum } from './types/router'
import { getConfiguration } from './utils/config'

import 'react-toastify/dist/ReactToastify.css'

function App() {
    const location = useLocation()
    const navigate = useNavigate()
    const { enabledTabs, defaultPage } = getConfiguration()

    useEffect(() => {
        if (location.pathname === '/') {
            navigate(defaultPage, { replace: true })
        }
    }, [location.pathname, navigate])

    return (
        <QueryParamProvider adapter={ReactRouter6Adapter}>
            <Routes>
                <Route path="/login" element={<Login />} />
                <Route
                    element={
                        <RequireAuth roles={[IRoleEnum.ADMIN, IRoleEnum.USER]}>
                            <Layout />
                        </RequireAuth>
                    }
                >
                    {enabledTabs.rules && (
                        <>
                            <Route path="/rules" element={<Rules />} />
                            <Route path="/rules/:ruleId" element={<Rules />} />
                        </>
                    )}
                    {enabledTabs.slotsForms && (
                        <Route path="/slots-forms" element={<SlotsForms />} />
                    )}
                    {enabledTabs.actions && (
                        <Route path="/actions" element={<Actions />} />
                    )}
                    {enabledTabs.configuration && (
                        <Route path="/config" element={<Configuration />} />
                    )}
                    {enabledTabs.messages && (
                        <>
                            <Route path="/messages" element={<Messages />} />
                            <Route
                                path="/messages/:messageId"
                                element={<Messages />}
                            />
                        </>
                    )}
                    {enabledTabs.conversations && (
                        <>
                            <Route
                                path="/conversations"
                                element={<Conversations />}
                            />
                            <Route
                                path="/conversations/:conversationId"
                                element={<Conversations />}
                            />
                        </>
                    )}
                    {enabledTabs.faq && <Route path="/faq" element={<Faq />} />}
                    {enabledTabs.messageTemplates && (
                        <Route
                            path="/message-templates"
                            element={<MessageTemplates />}
                        />
                    )}
                    {enabledTabs.nluData && (
                        <Route path="/training-data">
                            <Route
                                path="/training-data/data"
                                element={<NLUPage />}
                            />
                            <Route
                                path="/training-data/data/:tab"
                                element={<NLUPage />}
                            />
                            {enabledTabs.nluDataLookup && (
                                <Route
                                    path="/training-data/lookup"
                                    element={<Lookup />}
                                />
                            )}
                            {enabledTabs.nluDataStats && (
                                <Route
                                    path="/training-data/stats"
                                    element={<NLUStats />}
                                />
                            )}
                        </Route>
                    )}
                    {enabledTabs.responses && (
                        <Route path="/responses" element={<Responses />} />
                    )}
                    {enabledTabs.analytics && (
                        <Route path="/analytics" element={<Analytics />} />
                    )}
                    <Route path="/*" element={<NotFound />} />
                    <Route path="/account" element={<Account />} />
                </Route>
                <Route
                    element={
                        <RequireAuth roles={[IRoleEnum.ADMIN]}>
                            <Layout />
                        </RequireAuth>
                    }
                >
                    {enabledTabs.users && (
                        <Route path="/users" element={<Users />} />
                    )}
                    {enabledTabs.dataSources && (
                        <Route path="/data-sources" element={<DataSources />} />
                    )}
                    {enabledTabs.importExport && (
                        <Route path="/import-export" element={<Admin />} />
                    )}
                </Route>
            </Routes>
        </QueryParamProvider>
    )
}

export default App
